import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ModalProps,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

export type AmlConfirmModalAction = 'execute' | 'cancel' | '';

type AmlConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  confirmAction: AmlConfirmModalAction;
  onSuccess: (reason?: string, password?: string) => void;
  onCancel: () => void;
  isLoading: boolean;
};

export const AmlConfirmModal: FC<AmlConfirmModalProps> = (props) => {
  const { open, onClose, confirmAction, onSuccess, onCancel, isLoading } = props;
  const { t } = useTranslation();

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    setTotp('');
    setReason('');
    onClose();
  };
  const closeHandler = () => {
    setTotp('');
    setReason('');
    onClose();
  };

  // cancel handler
  const cancelHandler = () => {
    setTotp('');
    setReason('');
    onCancel();
  };

  // variables
  const isExecute = confirmAction === 'execute';

  const [totp, setTotp] = useState('');
  const [reason, setReason] = useState('');

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>
        {confirmAction
          ? isExecute
            ? t('transaction.modal.execute.title')
            : t('transaction.modal.cancel.title')
          : '-'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={closeHandler}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <div>
            {confirmAction
              ? isExecute
                ? t('transaction.modal.execute.text')
                : t('transaction.modal.cancel.text')
              : '-'}
          </div>
          <TextField
            name="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
            label={t('ui.cancel-modal.reason')}
            fullWidth
            multiline
            minRows={3}
          />

          <TextField
            name="password"
            value={totp}
            onChange={(e) => setTotp(e.target.value)}
            label={t('transit-addresses.add-withdraw-modal.fields.password')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={() => onSuccess(reason, totp)} disabled={isLoading}>
          {t('transaction.modal.yes')}
        </Button>
        <Button variant="outlined" onClick={() => cancelHandler()} disabled={isLoading}>
          {t('transaction.modal.no')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
